import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { userActive, useResumeRefresh } from "@/components/hooks/activityGate";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ContactSupportOutlinedIcon from "@mui/icons-material/ContactSupportOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import TroubleshootOutlinedIcon from "@mui/icons-material/TroubleshootOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Badge } from "@mui/material";
import { useAuth } from "contexts/AuthProvider";
import { UserAccountResponse } from "components/entities";
import { storageServiceApi } from "contexts/axiosApiInstance";
import {
  NotificationStatus,
  NotificationTypeJS,
  Notification,
  GetStorageProfileResponse,
  ResponseStatus,
} from "target_wasm";
import { useNavigation } from "contexts/NavigationProvider";
import { fetchAccountDetails } from "./models/ModelServices";
import { useBoundStore } from "components/canvas/stores/useAppStore";
import { createLogger } from "debug/DebugingLogger";
import { SidebarItems } from "./SidebarItems";
import type { SidebarItem } from "./SidebarItems";

import { UserProfileItem } from "./UserProfileItem";
import { NotificationDropdown } from "./notifications/NotificationDropdown";
import { fetchNotifications } from "./notifications/NotificationsApi";
import { Divider } from "components/common/Divider";
import { useThemedLogo } from "components/hooks/useThemedLogo";
import { useTeamsStore } from "@/components/main/stores/teamsStore";
import { useShallow } from "zustand/react/shallow";
import { useSupportStore } from "stores/useSupportStore";
import { StorageIndicator } from "./StorageIndicator";
import { SettingsDialog } from "./SettingsDialog";
import { OrgSwitcher } from "@/components/main/spaces/OrgSwitcher";
import { SpacesRail } from "@/components/main/spaces/SpacesRail";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { useSharedWithMeStore } from "@/components/main/stores/sharedWithMeStore";
import { scanAccessCards } from "@/components/main/utils/notifications/accessCardRefresh";
import {
  partitionSharedModels,
  selectExternalModels,
  sharedWithMeCount,
} from "@/components/main/sharedWithMe/selectors";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import "./common/VersionChip.css";

const log = createLogger("Sidebar");

/// Where a personal org's quota stops being background information and
/// starts being something to act on.
const QUOTA_WARNING_PERCENT = 80;

/// Cards that say "what you can reach changed", by the list each one moves.
/// A space grant moves both: it brings the space AND the models inside it.
const SPACE_LIST_CARDS = [NotificationTypeJS.SpaceAccess] as const;
const MODEL_LIST_CARDS = [
  NotificationTypeJS.SpaceAccess,
  NotificationTypeJS.ModelShared,
  NotificationTypeJS.AccessRevokedFromModel,
  NotificationTypeJS.ModelDeleted,
  NotificationTypeJS.InvitedToTeam,
  NotificationTypeJS.AccessRevokedFromTeam,
  NotificationTypeJS.TeamDeleted,
] as const;

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, toggleSidebar }) => {
  const sidebarWidthVar = isOpen
    ? "var(--sidebar-width-open)"
    : "var(--sidebar-width-closed)";
  const { selectedPage, navigateToPage, teamsUpdateTrigger } = useNavigation();

  const MycorrLogo = useThemedLogo();
  const { logout, email, role } = useAuth();
  const { activeOrgId, selectedSpaceId, setSelectedSpace, initOrgContext } =
    useOrgContextStore(
      useShallow((state) => ({
        activeOrgId: state.activeOrgId,
        selectedSpaceId: state.selectedSpaceId,
        setSelectedSpace: state.setSelectedSpace,
        initOrgContext: state.init,
      })),
    );
  useEffect(() => {
    void initOrgContext();
  }, [initOrgContext]);
  const invalidateModels = useMainStateStore((state) => state.invalidateModels);
  // An org switch re-scopes the whole models list (S6c narrowing).
  useEffect(() => {
    if (activeOrgId) void invalidateModels();
  }, [activeOrgId, invalidateModels]);
  const socket = useBoundStore((state) => state.websocket);

  const fetchTeamsList = useTeamsStore((state) => state.fetchTeamsList);

  const [showNotificationsDropdown, setShowNotificationsDropdown] =
    useState(false);
  const [notificationsDropdownPersistent, setNotificationsDropdownPersistent] =
    useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [accountDetails, setAccountDetails] = useState<
    UserAccountResponse | undefined
  >(undefined);
  const [storageProfile, setStorageProfile] =
    useState<GetStorageProfileResponse | null>(null);
  const [storageFetchState, setStorageFetchState] = useState<
    "loading" | "loaded" | "error"
  >("loading");
  const openSupport = useSupportStore((s) => s.openSupport);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<"general" | "notifications">(
    "general",
  );

  const notificationsButtonRef = useRef<HTMLButtonElement>(null);
  const navScrollRef = useRef<HTMLDivElement>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const notificationsTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const intervalRef = useRef<number | null>(null);
  const storageIntervalRef = useRef<number | null>(null);
  const seenAccessCardsRef = useRef<Set<string> | null>(null);

  const loadNotifications = async () => {
    try {
      const fetchedNotifications = await fetchNotifications();
      setNotifications(fetchedNotifications);
      dropRevokedOrgs(fetchedNotifications);
      refreshOnAccessCards(fetchedNotifications);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  /// A removal the caller did not make is otherwise invisible until reload:
  /// the org list is fetched at init, and polling it is O(org size) per call.
  /// The card is the signal — and the refetch only happens when the org it
  /// names is still on our list, so a card we have already acted on is free.
  const dropRevokedOrgs = (fetched: Notification[]) => {
    const revoked = fetched.some((n) => {
      if (n.notification_type_js() !== NotificationTypeJS.OrgAccessRevoked) {
        return false;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- WASM interop: get_data_js() returns untyped JS object
      const data = n.get_data_js() as any;
      return useOrgContextStore
        .getState()
        .orgs.some((o) => o.org_id === data?.org_id);
    });
    if (revoked) void useOrgContextStore.getState().refreshOrgs();
  };

  /// The space list is a snapshot taken at init and on org switch, and the
  /// models list polls only on the Overview and Shared-with-me pages — so a
  /// grant or revoke someone else made is invisible from anywhere else until
  /// reload. The card is the signal, exactly as it is for a revoked org above.
  const refreshOnAccessCards = (fetched: Notification[]) => {
    const idsOf = (types: readonly NotificationTypeJS[]): string[] =>
      fetched
        .filter((n) => types.includes(n.notification_type_js()))
        .map((n) => n.notification_id_js());
    const { refreshSpaces, refreshModels, seen } = scanAccessCards(
      idsOf(SPACE_LIST_CARDS),
      idsOf(MODEL_LIST_CARDS),
      seenAccessCardsRef.current,
    );
    seenAccessCardsRef.current = seen;

    if (refreshSpaces) {
      void useOrgContextStore.getState().refreshSpaces();
      // A guest grant (D26) lands in Shared-with-me, whose store fetches once
      // on mount and never polls — strictly staler than the rail.
      void useSharedWithMeStore.getState().refresh();
    }
    if (refreshModels) void invalidateModels();
  };

  const activeOrg = useOrgContextStore((s) =>
    s.orgs.find((o) => o.org_id === s.activeOrgId),
  );
  // D32: the Shared-with-me item shows only when something IS shared from
  // outside the caller's orgs — an empty page must not become a permanent
  // shelf in the rail. Models ride the existing overview poll; the granted
  // spaces come from their own identity-scoped store.
  const sharedSpaces = useSharedWithMeStore((s) => s.spaces);
  const sharedStatus = useSharedWithMeStore((s) => s.status);
  const refreshSharedWithMe = useSharedWithMeStore((s) => s.refresh);
  const availableModels = useMainStateStore((s) => s.availableModels);
  const sharedCount = useMemo(() => {
    const external = selectExternalModels(availableModels);
    const granted = new Set(sharedSpaces.map((sp) => sp.space_id));
    const { standalone } = partitionSharedModels(external, granted);
    return sharedWithMeCount(sharedSpaces.length, standalone.length);
  }, [availableModels, sharedSpaces]);
  useEffect(() => {
    if (navigator.onLine) void refreshSharedWithMe();
  }, [refreshSharedWithMe]);
  // The rail's quota widget is a WARNING, not a dashboard. A company org's
  // usage belongs on its settings page (Plan & usage), where the people who
  // can act on it are; here it would nag every member about a pool they
  // don't control. A personal org's owner IS the one who can act, so it
  // surfaces — but only once the pool is nearly gone.
  const isPersonalOrg = !!activeOrg?.is_personal;
  const storagePercent = storageProfile
    ? (Number(storageProfile.usage_bytes) /
        Number(storageProfile.quota_bytes)) *
      100
    : 0;
  const showsQuota = isPersonalOrg && storagePercent >= QUOTA_WARNING_PERCENT;

  const fetchStorageProfile = useCallback(async () => {
    // Nothing renders it for a company org, so don't poll for it.
    if (!isPersonalOrg) return;
    // A failure never downgrades numbers already on screen — the poll retries.
    const markFailed = () =>
      setStorageFetchState((prev) => (prev === "loaded" ? prev : "error"));
    try {
      // Quota keys on the ACTIVE org (S4a); the personal org is the server-side
      // default when none is known yet.
      const response = await storageServiceApi.get("get_storage_profile", {
        params: activeOrgId ? { org_id: activeOrgId } : undefined,
      });
      const responseData: GetStorageProfileResponse =
        new GetStorageProfileResponse(JSON.stringify(response.data));
      if (responseData.status !== ResponseStatus.Success) {
        log.error(
          "Storage profile request was not successful: %o",
          response.data,
        );
        markFailed();
        return;
      }
      setStorageProfile(responseData);
      setStorageFetchState("loaded");
    } catch (err) {
      log.error("Error fetching storage profile: %o", err);
      markFailed();
    }
  }, [activeOrgId, isPersonalOrg]);

  const refreshData = useCallback(() => {
    fetchTeamsList({ showLoading: false, refresh: true });
    loadNotifications();
  }, [fetchTeamsList]);

  useEffect(() => {
    setStorageProfile(null);
    setStorageFetchState("loading");
    if (navigator.onLine) {
      refreshData();
      fetchStorageProfile();
    }

    // Idle-only gate: notifications stay fresh in hidden tabs until the
    // user goes idle (the browser throttles hidden-tab timers anyway).
    intervalRef.current = window.setInterval(() => {
      if (!navigator.onLine || !userActive()) return;
      refreshData();
    }, 30_000);
    storageIntervalRef.current = window.setInterval(() => {
      if (!navigator.onLine || !userActive()) return;
      fetchStorageProfile();
    }, 60000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (storageIntervalRef.current) {
        clearInterval(storageIntervalRef.current);
        storageIntervalRef.current = null;
      }
    };
  }, [refreshData, fetchStorageProfile]);

  useResumeRefresh(
    useCallback(() => {
      if (navigator.onLine) {
        refreshData();
        fetchStorageProfile();
        // The backstop for every door that changes what a space list holds
        // without dealing a card — a team roster edit, a classification
        // change, the inheritors a seal cuts off. Deliberately NOT in
        // `refreshData`, which is also the 30s tick: the space read costs an
        // FGA ListObjects per call, which is the exact cost this design
        // exists to pay only when something changed.
        void useOrgContextStore.getState().refreshSpaces();
        void useSharedWithMeStore.getState().refresh();
      }
    }, [refreshData, fetchStorageProfile]),
  );

  useEffect(() => {
    const handleNotificationsRefresh = () => {
      loadNotifications();
    };
    window.addEventListener(
      "notifications-refresh",
      handleNotificationsRefresh,
    );
    return () => {
      window.removeEventListener(
        "notifications-refresh",
        handleNotificationsRefresh,
      );
    };
  }, []);

  useEffect(() => {
    if (teamsUpdateTrigger !== undefined && teamsUpdateTrigger > 0) {
      fetchTeamsList({ showLoading: false, refresh: true });
    }
  }, [teamsUpdateTrigger, fetchTeamsList]);

  useEffect(() => {
    fetchAccountDetails(email, setAccountDetails);
  }, [email]);

  const profilePhoto = accountDetails?.account_details?.photo || "";

  // The flyout is fixed-positioned from its trigger's rect at open time, so
  // anything that moves that trigger strands it beside the wrong row.
  useEffect(() => {
    const scroller = navScrollRef.current;
    if (!scroller) return;
    const closeFlyouts = () => {
      setShowNotificationsDropdown(false);
      setNotificationsDropdownPersistent(false);
    };
    scroller.addEventListener("scroll", closeFlyouts, { passive: true });
    window.addEventListener("resize", closeFlyouts);
    return () => {
      scroller.removeEventListener("scroll", closeFlyouts);
      window.removeEventListener("resize", closeFlyouts);
    };
  }, []);

  const handleNotificationsClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setNotificationsDropdownPersistent((prev) => {
      const next = !prev;
      setShowNotificationsDropdown(next);
      // An org-invite card carries no state of its own — whether it is still
      // answerable lives in the invitations list, which is otherwise only
      // fetched at app init. Without this, an invite that arrives while the
      // app is open renders its card with no Accept/Decline.
      if (next) void useOrgContextStore.getState().refreshInvites();
      return next;
    });
  };

  const handleNotificationsClose = () => {
    setShowNotificationsDropdown(false);
    setNotificationsDropdownPersistent(false);
  };

  const handleNotificationsDropdownEnter = () => {
    if (notificationsTimeoutRef.current)
      clearTimeout(notificationsTimeoutRef.current);
  };

  const handleNotificationsDropdownLeave = () => {
    if (!notificationsDropdownPersistent) setShowNotificationsDropdown(false);
  };

  const handleNotificationClick = (_notificationId: string) => {
    loadNotifications();
  };

  const handleLogout = () => {
    socket?.close();
    logout();
  };

  const hasUnreadNotifications = notifications.some(
    (notification) => notification.status_js() === NotificationStatus.Pending,
  );

  const sidebarItem = (
    Icon: React.ComponentType<{ className?: string }>,
    text: string,
    page?: string,
    overrides?: Partial<SidebarItem>,
  ): SidebarItem => ({
    icon: <Icon className="icon-md cursor-default" />,
    text,
    tooltip: text,
    page,
    ...overrides,
  });

  const notificationsIcon = (
    <Badge
      color="primary"
      variant="dot"
      invisible={!hasUnreadNotifications}
      overlap="circular"
      sx={{
        "& .MuiBadge-dot": {
          backgroundColor: "var(--color-accent)",
          width: "6px !important",
          height: "6px !important",
          minWidth: "6px",
          minHeight: "6px",
          borderRadius: "50%",
        },
      }}
    >
      <NotificationsOutlinedIcon className="icon-md cursor-default" />
    </Badge>
  );

  const mainMenuItems: SidebarItem[] = [
    {
      ...sidebarItem(NotificationsOutlinedIcon, "Notifications", undefined),
      icon: notificationsIcon,
      hasDropdown: true,
      onClick: handleNotificationsClick,
      buttonRef: notificationsButtonRef,
      isActive: notificationsDropdownPersistent,
    },
    sidebarItem(DashboardOutlinedIcon, "Overview", "Overview"),
    ...(sharedStatus === "ready" && sharedCount > 0
      ? [
          sidebarItem(
            FolderSharedOutlinedIcon,
            "Shared with me",
            "SharedWithMe",
          ),
        ]
      : []),
    sidebarItem(LayersOutlinedIcon, "Datasets", "Datasets"),
    sidebarItem(StorageOutlinedIcon, "Connections", "Connections"),
    ...(role === "mycor_dev_team"
      ? [
          {
            ...sidebarItem(AdminPanelSettingsOutlinedIcon, "Admin"),
            isActive: selectedPage.startsWith("Admin"),
            // A side flyout like Teams, in both rail modes — the chevron
            // points the way it opens.
            rightAction: <ChevronRightIcon className="icon-sm" />,
            children: [
              sidebarItem(
                ListAltOutlinedIcon,
                "Invitations",
                "AdminInvitations",
              ),
              sidebarItem(PeopleOutlineIcon, "Accounts", "AdminAccounts"),
              sidebarItem(
                PersonOffOutlinedIcon,
                "Removed",
                "AdminRemovedAccounts",
              ),
              sidebarItem(
                SmartToyOutlinedIcon,
                "Service Accounts",
                "AdminServiceAccounts",
              ),
              sidebarItem(
                HistoryOutlinedIcon,
                "Data Lifecycle",
                "AdminDataLifecycle",
              ),
              sidebarItem(
                TroubleshootOutlinedIcon,
                "Audit Tooling",
                "AdminAudit",
              ),
              // Env-specific Grafana; unset locally, so the item only exists
              // on deployed builds.
              ...(import.meta.env.VITE_APP_MONITORING_URL
                ? [
                    {
                      ...sidebarItem(QueryStatsOutlinedIcon, "Monitoring"),
                      onClick: () =>
                        window.open(
                          import.meta.env.VITE_APP_MONITORING_URL,
                          "_blank",
                          "noopener,noreferrer",
                        ),
                      rightAction: <OpenInNewIcon className="icon-sm" />,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
  ];

  const supportMenuItems: SidebarItem[] = [
    sidebarItem(DeleteOutlinedIcon, "Trash", "Trash"),
    sidebarItem(VpnKeyOutlinedIcon, "Access", "Access"),
    sidebarItem(ContactSupportOutlinedIcon, "Support", undefined, {
      onClick: (e) => openSupport(e?.currentTarget.getBoundingClientRect().top),
    }),
  ];

  const handleItemClick = (page: string) => {
    navigateToPage(page);
  };

  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
      if (notificationsTimeoutRef.current) {
        clearTimeout(notificationsTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div
        id="main-sidebar"
        className="sidebar"
        style={{
          width: "100%",
          boxShadow: isOpen
            ? "4px 0 12px -2px rgba(0, 0, 0, 0.06)"
            : "0 0 0 0 rgba(0, 0, 0, 0)",
        }}
      >
        {/* Logo and link share one centered flex row, so the link growing on
            hover shifts the logo left and keeps the pair centered. */}
        <div
          className={`logo-area flex justify-center items-center item-height shrink-0${
            isOpen ? " logo-area--open" : ""
          }`}
        >
          <button
            onClick={toggleSidebar}
            className="flex justify-center items-center cursor-pointer min-w-8 min-h-8"
          >
            <MycorrLogo className="h-8" aria-label="Logo" />
          </button>
          <a
            href="https://mycorr.app"
            target="_blank"
            rel="noopener noreferrer"
            className="info-chip logo-link"
            aria-hidden={!isOpen}
            tabIndex={isOpen ? 0 : -1}
          >
            <div className="info-chip__text">
              <span
                className="flex items-center"
                style={{ gap: "var(--spacing-xs)" }}
              >
                <span>mycorr.app</span>
                <OpenInNewIcon sx={{ fontSize: 10 }} />
              </span>
            </div>
          </a>
        </div>

        <div className="flex flex-col h-full overflow-hidden">
          {isOpen && <OrgSwitcher />}
          {/* The rail's only scroller. `min-h-0` is the load-bearing part: a
              flex child defaults to min-content, so without it a long nav (or
              a deep space tree) pushes the pinned footer out of the rail
              instead of scrolling. */}
          <div
            ref={navScrollRef}
            className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto scrollbar-overlay-hover"
          >
            <div className="mt-6">
              <SidebarItems
                items={mainMenuItems}
                isOpen={isOpen}
                activeItem={selectedPage}
                onItemClick={handleItemClick}
                sidebarWidth={sidebarWidthVar}
              />
            </div>
            {isOpen && (
              <div className="mt-6">
                <SpacesRail
                  selectedSpaceId={selectedSpaceId}
                  onSelectSpace={setSelectedSpace}
                />
              </div>
            )}
          </div>
          <Divider />
          <SidebarItems
            items={supportMenuItems}
            isOpen={isOpen}
            activeItem={selectedPage}
            onItemClick={handleItemClick}
          />
          <Divider />
          {showsQuota && (
            <>
              <StorageIndicator
                isOpen={isOpen}
                storageProfile={storageProfile}
                fetchState={storageFetchState}
                onSupportClick={openSupport}
                sidebarWidth={sidebarWidthVar}
              />
              <Divider />
            </>
          )}
          <UserProfileItem
            isOpen={isOpen}
            email={email}
            accountDetails={accountDetails}
            profilePhoto={profilePhoto}
            onLogout={handleLogout}
            onOpenSettings={() => {
              setSettingsTab("general");
              setSettingsOpen(true);
            }}
            sidebarWidth={sidebarWidthVar}
          />
        </div>
      </div>

      <div
        onMouseEnter={handleNotificationsDropdownEnter}
        onMouseLeave={handleNotificationsDropdownLeave}
      >
        <NotificationDropdown
          notifications={notifications}
          isVisible={showNotificationsDropdown}
          onClose={handleNotificationsClose}
          onNotificationClick={handleNotificationClick}
          onNotificationUpdate={loadNotifications}
          triggerRef={notificationsButtonRef as React.RefObject<HTMLElement>}
          sidebarWidth={sidebarWidthVar}
          onOpenSettings={() => {
            setSettingsTab("notifications");
            setSettingsOpen(true);
          }}
        />
      </div>

      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        initialTab={settingsTab}
        accountDetails={accountDetails}
        onAccountUpdated={() => fetchAccountDetails(email, setAccountDetails)}
      />
    </>
  );
};
